/**
 * /operations/travel/per-diem-disbursements — Session 3B PR 20.
 * Owning agent: Rawatib. Per-diem advance disbursement queue.
 */

import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Layers, PiggyBank, ShieldAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { PerDiemDisbursementItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

type StateFilter = "all" | "pending" | "approved" | "disbursed";

function _stateToneClass(s: PerDiemDisbursementItem["disbursement_state"]): string {
  if (s === "disbursed") return "bg-emerald-100 text-emerald-800";
  if (s === "approved") return "bg-brand-blue/15 text-brand-deep";
  return "bg-amber-100 text-amber-800";
}

function _fmtSar(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function PerDiemDisbursementsPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [stateFilter, setStateFilter] = useState<StateFilter>("pending");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const list = useQuery({
    queryKey: ["session3b-per-diem", legalEntityId, stateFilter],
    queryFn: () =>
      session3bApi.listPerDiemDisbursements(
        legalEntityId ?? "",
        stateFilter === "all" ? undefined : stateFilter,
      ),
    enabled: !!legalEntityId,
  });

  const items: PerDiemDisbursementItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const pending = items.filter((r) => r.disbursement_state === "pending");
    const sumPending = pending.reduce((a, r) => a + r.total_advance_sar, 0);
    return {
      total: items.length,
      attention: pending.length,
      critical: 0,
      sumPending,
    };
  }, [items]);

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("travel.per_diem.title", "Per Diem Disbursements")}
          subtitle={t(
            "travel.per_diem.subtitle",
            "Per-diem advance queue routing through Off-Cycle disbursement. Mutaba'aa watches Article 90 cap interaction with active loans.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(["pending", "approved", "disbursed", "all"] as StateFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setStateFilter(f)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  stateFilter === f
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`travel.per_diem.filter.${f}`, f)}
              </button>
            ))}
          </div>
          <button
            type="button"
            disabled={selected.size === 0}
            onClick={() =>
              showAlertTrace(
                t(
                  "travel.per_diem.cta.batch_note",
                  `Batch disburse ${selected.size} advance${selected.size === 1 ? "" : "s"} via the off-cycle path — single bank file batch.`,
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95 disabled:opacity-40"
          >
            <Layers className="h-4 w-4" />
            {t("travel.per_diem.cta.batch", "Batch disburse")} ({selected.size})
          </button>
        </div>

        {counts.attention > 0 && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<PiggyBank className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "travel.per_diem.suggestion.pending_headline",
              `${counts.attention} pending per-diem advance${counts.attention === 1 ? "" : "s"} · total SAR ${_fmtSar(counts.sumPending)}.`,
            )}
            body={t(
              "travel.per_diem.suggestion.pending_body",
              "Rawatib suggests batching — one bank file covers them all. Mutaba'aa pre-checks Article 90 cap interaction with each traveler's active loans.",
            )}
            primaryAction={{
              label: t("travel.per_diem.suggestion.select_all", "Select all pending"),
              onClick: () => {
                setSelected(new Set(items.filter((r) => r.disbursement_state === "pending").map((r) => r.disbursement_id)));
              },
            }}
            suggestionKey="per-diem-pending"
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<CheckCircle2 className="h-4 w-4" />}
            severity="info"
            headline={t(
              "travel.per_diem.suggestion.empty_headline",
              "No per-diem advances pending.",
            )}
            body={t(
              "travel.per_diem.suggestion.empty_body",
              "Rawatib notes: advances queue here when travel requests are approved and the M3X.TRAVEL per-diem matrix fires.",
            )}
            primaryAction={{
              label: t("travel.per_diem.suggestion.go_active", "Active travelers"),
              onClick: () => window.location.assign("/operations/travel/active-travelers"),
            }}
            suggestionKey="per-diem-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-3 py-2">{t("travel.per_diem.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("travel.per_diem.col.destination", "Destination")}</th>
                  <th className="px-3 py-2">{t("travel.per_diem.col.days", "Days")}</th>
                  <th className="px-3 py-2">{t("travel.per_diem.col.rate", "Daily rate (SAR)")}</th>
                  <th className="px-3 py-2">{t("travel.per_diem.col.advance", "Total advance (SAR)")}</th>
                  <th className="px-3 py-2">{t("travel.per_diem.col.state", "State")}</th>
                  <th className="px-3 py-2">{t("travel.per_diem.col.bank", "Bank")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.disbursement_id} className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(row.disbursement_id)}
                        onChange={() => toggleSelect(row.disbursement_id)}
                        disabled={row.disbursement_state !== "pending"}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{row.destination}</td>
                    <td className="px-3 py-2 text-text-secondary">{row.per_diem_days}</td>
                    <td className="px-3 py-2 font-mono text-xs">{_fmtSar(row.daily_rate_sar)}</td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "travel.per_diem.trace.advance",
                              `total_advance_sar = per_diem_days (${row.per_diem_days}) × daily_rate_sar (${row.daily_rate_sar.toFixed(2)}). Daily rate comes from the M3X.TRAVEL per-diem matrix (category × destination).`,
                            ),
                          )
                        }
                      >
                        <span className="font-mono">{_fmtSar(row.total_advance_sar)}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_stateToneClass(row.disbursement_state)}`}
                      >
                        {row.disbursement_state}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span className="inline-flex items-center gap-1">
                        <ShieldAlert className="h-3 w-3" />
                        {row.bank_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="rawatib"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: 0,
            top_kind: t("travel.per_diem.title", "Per Diem Disbursements"),
            window_label: `pending SAR ${_fmtSar(counts.sumPending)}`,
          }}
        />
      </aside>
    </div>
  );
}
